import { ApiService } from "./api.service";
import { Injectable } from '@angular/core';
import { Observable, throwError } from "rxjs";
import { HttpClient } from '@angular/common/http';
import { catchError, timeout, map } from 'rxjs/operators';
import { BaseService } from "./base.service";
import { QuoteDetails, QuoteAssistV2Data, PartDetail, CostBreakdown, RfqSource } from "../app/quoteAssist-V2/quote-assist-v2.model";
import { environment } from '../environments/environment';
import { QuoteStatus } from "../app/shared/enums";

export interface SaveQuoteDetailsResponse {
  success: boolean;
  data: {
    rfqId: string;
    message?: string;
  };
  error?: string;
}

export interface GetQuoteDataV2Response {
  success: boolean;
  data: QuoteAssistV2Data;
  error?: string;
}

export interface SubmitQuoteV2Response {
  success: boolean;
  data: {
    quoteId: string;
    message?: string;
  };
  error?: string;
}

export interface SaveFeedbackRequest {
  rfqId: string;
  companyId: string;
  contactId: number;
  surveyFeedback: string;
  surveyComments?: string|undefined;
}

export interface SaveFeedbackResponse {
  data: boolean;
  message: string;
  errors: string[] | null;
}

// New interfaces for integration-specific responses
export interface MarketplaceRfqResponse {
  status: boolean;
  data: {
    companyId: number;
    supplierId: number;
    rfqId: number;
    quoteStatus: string;
    rfqPartsMetaData: string;
    createdOn: string;
    source: string;
  };
  message: string;
  errors: string[];
}

export interface DirectRfqResponse {
  status: boolean;
  data: {
    companyId: number;
    companyGuid: string | null;
    sequenceId: string | null;
    supplierId: number;
    rfqId: string;
    quoteStatus: string;
    rfqPartsMetaData: string;
    createdOn: string;
    submittedOn: string | null;
    source: string;
  };
  message: string;
  errors: string[];
}

export interface FileUploadResponse {
  success: boolean;
  data: {
    rfqId: string;
    fileUrls?: string[];
    extractedContent?: string;
    rfqPartDetails?: any[]; // Add rfqPartDetails from API response
    message?: string;
  };
  error?: string;
}

export interface MetadataExtractionResponse {
  success: boolean;
  data: {
    extractedContent: string;
    message?: string;
  };
  error?: string;
}

export interface CostBreakdownResponse {
  success: boolean;
  data: {
    costBreakdown: any;
    message?: string;
  };
  error?: string;
}
export interface SurveyFeedbackExistsResponse {
  status: boolean;
  data: boolean;
  message: string;
  errors: string[];
}

export interface RfqVersionResponse {
    status: boolean;
    data: number;
    message: string;
    errors: string[];
}

// New interface for Marketplace Draft (PascalCase to match API sample)
export interface MarketplaceDraftModel {
  CompanyId: number;
  SupplierId: number;
  RfqId: number;
  QuoteStatus?: string;
  RfqPartsMetaData?: string;
  CreatedOn?: string;
  Source?: string;
  BuyerName?: string;
  BuyerCompanyEmail?: string;
  // Additional properties as requested (lower camelCase)
  quoteExpiryDate?: string;
  paymentTerm?: string;
  quoteNumber?: string;
}

// New interface for V2 API request
export interface SaveQASupplierRfqQuoteDataV2Request {
  companyId: number;
  supplierId: number;
  rfqId: number;
  buyerName: string;
  buyerCompanyEmail: string;
  quoteStatus: string;
  rfqPartsMetaData: string;
  createdOn: string;
  source: string;
}

export interface SaveMarketplaceDraftResponse {
  success: boolean;
  data?: any;
  error?: string;
  message?: string;
}

export interface SendQuoteEmailToBuyerRequest {
  companyId: number;
  buyerCompanyEmail: string;
  supplierId: number;
  rfqId: string;
  quoteStatus: string;
}

export interface SendQuoteEmailToBuyerResponse {
  success: boolean;
  data?: any;
  error?: string;
  message?: string;
}

export interface PdfDownloadParams {
  version: number;
  companyId: number;
  rfqId: string;
  contactId: number;
}

/** Request payload for GetRfqQuoteIntelligence (quote intelligence / risks per part) */
export interface GetRfqQuoteIntelligenceRequest {
  RfqId: number;
  RfqPartId: number[];
  CompanyId: number;
  ContactId: number;
}

/** Response from GetRfqQuoteIntelligence - matches API shape (cardData is stringified JSON) */
export interface GetRfqQuoteIntelligenceResponse {
  rfqId: number;
  rfqPartId: number;
  contactId?: number;
  cardName: string;
  cardData: string;
  action: string;
}

/** Request payload for SaveCardFeedback (like/dislike on risk card) */
export interface SaveCardFeedbackRequest {
  rfqId: number;
  rfqPartId: number;
  contactId?: number;
  cardName: string;
  cardData: string;
  action: 'like' | 'dislike';
}

export interface SurveyFeedbackExistsResponse {
  status: boolean;
  data: boolean;
  message: string;
  errors: string[];
}

@Injectable({
    providedIn: 'root'
})
export class QuoteAssistV2Service extends BaseService {
   
    private extractedPdfJsonPayload: string | null = null;
    private baseApiUrl: string;

    constructor(
        private apiService: ApiService,
        private http: HttpClient
    ) {
        super();
        this.baseApiUrl = environment.APIEndpoint;
    }

    /**
     * Save quote details to the backend
     * @param formData FormData object containing quote details
     * @returns Observable with response containing rfqId
     */
    saveQuoteDetails(formData: FormData): Observable<SaveQuoteDetailsResponse> {
        return new Observable<SaveQuoteDetailsResponse>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'RFQ/SaveDirectRFQDetailsV2',
                    formData
                ).subscribe({
                    next: (response) => {
                        const result: SaveQuoteDetailsResponse = {
                            success: true,
                            data: {
                                rfqId: response.data?.rfqId?.toString() || '',
                                message: response.message || 'Quote details saved successfully'
                            }
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: SaveQuoteDetailsResponse = {
                            success: false,
                            data: {
                                rfqId: '',
                                message: 'Failed to save quote details'
                            },
                            error: err.message || 'Unknown error occurred'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: SaveQuoteDetailsResponse = {
                    success: false,
                    data: {
                        rfqId: '',
                        message: 'Failed to save quote details'
                    },
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    createFormDataFromQuoteDetails(
        quoteDetails: QuoteDetails, 
        companyId: number, 
        sourceType: RfqSource,
        rfqMetaDataSUW: any
    ): FormData {
        const formData = new FormData();

        // Required fields
        formData.append('CompanyId', companyId.toString());
        
        if (quoteDetails.rfqId && quoteDetails.rfqId.toString() !== '') {
            formData.append('RfqId', quoteDetails.rfqId.toString());
        }
        
        formData.append('RfqName', quoteDetails.rfqName);
        formData.append('CreatedOn', new Date().toISOString());
        formData.append('Source', sourceType);
        
        // Company details
        formData.append('BuyerCompanyName', quoteDetails.companyName);
        formData.append('BuyerCompanyAddress', quoteDetails.address);
        formData.append('BuyerCompanyEmail', quoteDetails.companyEmail);
        formData.append('BuyerCompanyPhone', quoteDetails.companyPhone);
        formData.append('BuyerAddress', quoteDetails.companyPhone);
        
        // Quote details
        formData.append('QuoteNumber', quoteDetails.quoteNumber);
        formData.append('QuoteExpiryDate', quoteDetails.quoteExpiryDate);
        formData.append('QuoteDate', quoteDetails.quoteDate || '');
        formData.append('PaymentTerm', quoteDetails.paymentTerm);
        
        // Contact ID
        if (quoteDetails.contactId) {
            formData.append('ContactId', quoteDetails.contactId.toString());
        }
        
        // Address details
        formData.append('BuyerCompanyCity', quoteDetails.city);
        formData.append('BuyerCompanyState', quoteDetails.state);
        formData.append('BuyerCompanyZipCode', quoteDetails.zipCode);
        formData.append('RfqMetaData', rfqMetaDataSUW);

        return formData;
    }

    validateQuoteDetails(quoteDetails: QuoteDetails): { isValid: boolean; errors: string[]; fieldErrors: { [fieldName: string]: string } } {
        const errors: string[] = [];
        const fieldErrors: { [fieldName: string]: string } = {};

        // RFQ Name validation
        if (!quoteDetails.rfqName?.trim() || quoteDetails.rfqName === 'Enter RFQ Name') {
            errors.push('RFQ Name');
            fieldErrors['rfqName'] = 'RFQ Name is required';
        }

        // Company Name validation
        if (!quoteDetails.companyName?.trim()) {
            errors.push('Company Name');
            fieldErrors['companyName'] = 'Company Name is required';
        }

        // Company Email validation
        if (!quoteDetails.companyEmail?.trim()) {
            errors.push('Company Email');
            fieldErrors['companyEmail'] = 'Company Email is required';
        } else {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(quoteDetails.companyEmail)) {
                errors.push('Valid Email Address');
                fieldErrors['companyEmail'] = 'Please enter a valid email address';
            }
        }

        // Company Phone validation
        if (!quoteDetails.companyPhone?.trim()) {
            errors.push('Company Phone Number');
            fieldErrors['companyPhone'] = 'Company Phone Number is required';
        } else {
            if (quoteDetails.companyPhone.length > 15) {
                errors.push('Valid Phone Number');
                fieldErrors['companyPhone'] = 'Please enter a valid phone number';
            }
        }

        // Quote Number validation
        if (!quoteDetails.quoteNumber?.trim()) {
            errors.push('Quote Number');
            fieldErrors['quoteNumber'] = 'Quote Number is required';
        }

        // Quote Expiry Date validation
        if (!quoteDetails.quoteExpiryDate) {
            errors.push('Valid Until Date');
            fieldErrors['quoteExpiryDate'] = 'Quote expiry date is required';
        }

        // Payment Term validation
        if (!quoteDetails.paymentTerm) {
            errors.push('Payment Terms');
            fieldErrors['paymentTerm'] = 'Payment Terms is required';
        }

        // Address validation (required for self-created RFQs)
        if (!quoteDetails.address?.trim()) {
            errors.push('Address');
            fieldErrors['address'] = 'Address is required';
        }

        if (!quoteDetails.city?.trim()) {
            errors.push('City');
            fieldErrors['city'] = 'City is required';
        }

        if (!quoteDetails.state?.trim()) {
            errors.push('State');
            fieldErrors['state'] = 'State is required';
        }

        if (!quoteDetails.zipCode?.trim()) {
            errors.push('Zip Code');
            fieldErrors['zipCode'] = 'Zip Code is required';
        }

        return {
            isValid: errors.length === 0,
            errors,
            fieldErrors
        };
    }

    getQuoteDataV2(rfqId: string, source: string): Observable<QuoteAssistV2Data> {
        return new Observable<QuoteAssistV2Data>(observer => {
            try {
                this.apiService.get<any>(
                    this.dynamoBaseApiUrl,
                    `RFQ/GetQuoteDataV2?rfqId=${rfqId}&source=${source}`
                ).subscribe({
                    next: (response) => {
                        if (response.success) {
                            observer.next(response.data);
                        } else {
                            observer.error(new Error(response.error || 'Failed to get quote data'));
                        }
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(new Error(err.message || 'Failed to get quote data'));
                    }
                });
            } catch (error) {
                observer.error(new Error(error instanceof Error ? error.message : 'Unknown error occurred'));
            }
        });
    }

    submitQuoteV2(rfqId: string): Observable<SubmitQuoteV2Response> {
        return new Observable<SubmitQuoteV2Response>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'RFQ/SubmitQuoteV2',
                    { rfqId }
                ).subscribe({
                    next: (response) => {
                        const result: SubmitQuoteV2Response = {
                            success: true,
                            data: {
                                quoteId: response.data?.quoteId || '',
                                message: response.message || 'Quote submitted successfully'
                            }
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: SubmitQuoteV2Response = {
                            success: false,
                            data: {
                                quoteId: '',
                                message: 'Failed to submit quote'
                            },
                            error: err.message || 'Unknown error occurred'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: SubmitQuoteV2Response = {
                    success: false,
                    data: {
                        quoteId: '',
                        message: 'Failed to submit quote'
                    },
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    draftDirectRFQQuoteData(quoteAssistData: QuoteAssistV2Data, companyId: number, contactId: number, source: RfqSource, 
        quoteStatus: QuoteStatus = QuoteStatus.Draft,
        BuyerName: string = '', BuyerCompanyEmail: string = ''): Observable<number> {
        const payload = {
            CompanyId: companyId,
            SupplierId: contactId,
            RfqId: quoteAssistData.rfqId,
            QuoteStatus: quoteStatus,
            RfqPartsMetaData: JSON.stringify(quoteAssistData),
            CreatedOn: new Date().toISOString(),
            Source: source,
            BuyerName: BuyerName,
            BuyerCompanyEmail: BuyerCompanyEmail
        };

        return new Observable<number>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierDirectRfqQuoteDataV2',
                    payload
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Fetch draft data from API (following V1 pattern)
     * @param rfqId RFQ ID
     * @param source Source type
     * @returns Observable with draft data
     */
    fetchDraft(rfqId: string, source: string): Observable<any> {
        return new Observable(observer => {
            this.apiService.get<any>(
                this.dynamoBaseApiUrl,
                `RFQ/GetDirectRFQDraft?rfqId=${rfqId}&source=${source}`
            ).subscribe({
                next: (response) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (err) => {
                    observer.error(err);
                }
            });
        });
    }

    /**
     * Fetch draft data from API using V1-style method (for MFG integration compatibility)
     * @param companyId Company ID
     * @param rfqId RFQ ID
     * @returns Observable with draft data
     */
    fetchDraftV1Style(companyId: number, rfqId: string): Observable<any> {
        return new Observable(observer => {
            this.apiService.get<any>(
                this.dynamoBaseApiUrl,
                `supplier/GetQASupplierRfqQuoteDataV2?rfqId=${rfqId}&companyId=${companyId}`
            ).subscribe({
                next: (response) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (err) => {
                    console.error("Error fetching draft data:", err);
                    observer.error(err);
                }
            });
        });
    }

    /**
     * Fetch complete quote assist V2 data from backend
     * @param rfqId RFQ ID
     * @param source Source type
     * @returns Observable with complete quote data
     */
    getQuoteAssistV2Data(rfqId: string, source: string): Observable<any> {
        return new Observable(observer => {
            try {
                this.apiService.get<any>(
                    this.dynamoBaseApiUrl,
                    `RFQ/GetQuoteAssistV2Data?rfqId=${rfqId}&source=${source}`
                ).subscribe({
                    next: (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Get MFG integration data (Marketplace RFQs)
     * @param rfqId RFQ ID
     * @param companyId Company ID
     * @returns Observable with marketplace RFQ data
     */
    getMfgIntegrationData(rfqId: string, companyId: number): Observable<MarketplaceRfqResponse> {
        return new Observable<MarketplaceRfqResponse>(observer => {
            try {
                this.apiService.get<any>(
                    this.dynamoBaseApiUrl,
                    `RFQ/GetMarketplaceRfqData?rfqId=${rfqId}&companyId=${companyId}`
                ).subscribe({
                    next: (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Get Direct integration data (Direct RFQs)
     * @param rfqId RFQ ID
     * @param companyId Company ID
     * @param integration Integration type (MFGDirect or SUW)
     * @returns Observable with direct RFQ data
     */
    getDirectIntegrationData(rfqId: string, companyId: number, integration: RfqSource): Observable<DirectRfqResponse> {
        return new Observable<DirectRfqResponse>(observer => {
            try {
                this.apiService.get<any>(
                    this.dynamoBaseApiUrl,
                    `supplier/GetQASupplierDirectRfqQuoteDatav2?rfqId=${rfqId}&companyId=${companyId}`
                ).subscribe({
                    next: (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Transform marketplace RFQ data to QuoteAssistV2Data format
     * @param response Marketplace RFQ response
     * @returns Transformed QuoteAssistV2Data
     */
    transformMarketplaceData(response: MarketplaceRfqResponse): QuoteAssistV2Data {
        try {
            const metaData = JSON.parse(response.data.rfqPartsMetaData);
            const rfqPartList = metaData.rfqPartList || [];
            const supplierMetaData = metaData.supplierMetaData || {};
            const rfqMetaData = metaData.rfqMetaData || [];

            // Transform quote details
            const quoteDetails: QuoteDetails = {
                rfqId: response.data.rfqId.toString(),
                rfqName: metaData.rfqName || '',
                companyName: supplierMetaData.companyName || '',
                companyEmail: metaData.buyerEmail || '',
                companyPhone: '',
                address: metaData.buyerAddress || '',
                city: '',
                state: '',
                zipCode: supplierMetaData.zipCode || '',
                quoteNumber: metaData.quoteNumber || '',
                quoteExpiryDate: metaData.quoteExpiryDate || '',
                paymentTerm: metaData.paymentTerm || '',
                companyId: response.data.companyId,
                contactId: response.data.supplierId
            };

            // Transform part details
            const partDetails: PartDetail[] = rfqPartList.map((part: any, index: number) => {
                const metaDataPart = rfqMetaData[index] || {};
                const quoteAssistData = part.quoteAssistData || {};
                const quoteAssistModel = quoteAssistData.quoteAssistModel || {};

                return {
                    partName: part.partName || '',
                    partNumber: part.rfqPartId?.toString() || '',
                    files: (part.pdfFiles || []).map((file: any) => ({
                        fileName: file.fileUrl?.split('/').pop() || '',
                        fileType: 'pdf',
                        fileURL: file.fileUrl || ''
                    })),
                    notesForBuyer: part.partDescription || '',
                    internalNotes: part.internalNotes || '',
                    quantityPricing: (part.partQuantity || []).map((qty: any) => ({
                        quantity: qty.qty || 0,
                        leadtimeValue: qty.leadTime ? parseInt(qty.leadTime) : undefined,
                        leadtimeUnit: 'Days' as const,
                        unitCost: qty.summaryData?.pricePerUnit?.totalPrice || 0,
                        markup: null,
                        unitPrice: qty.summaryData?.pricePerUnit?.totalPrice || 0,
                        total: (qty.qty || 0) * (qty.summaryData?.pricePerUnit?.totalPrice || 0),
                        unitPriceTotal: (qty.qty || 0) * (qty.summaryData?.pricePerUnit?.totalPrice || 0),
                        isLeadtimeEditing: qty.qty > 0 ? true : false,
                        order: 0, // Will be set by the component
                        isQuantityEditable: true, // Default to editable
                        isValidQuantity: (qty.qty || 0) > 0, // Valid if quantity > 0
                        showLeadTimeInputFields: false // Default to not showing input fields
                    })),
                    configuration: {
                        materials: (quoteAssistModel.materials?.materialsList || []).map((material: any) => ({
                            name: material.name || '',
                            cost: material.totalMaterialCost || 0,
                            costType: 'Per Unit' as const,
                            reference: material.description || ''
                        })),
                        tools: (quoteAssistModel.tooling?.toolingList || []).map((tool: any) => ({
                            name: tool.toolName || '',
                            cost: tool.estimatedCost || 0,
                            costType: 'Fixed' as const,
                            reference: tool.purpose || ''
                        })),
                        processes: (quoteAssistModel.manufacturingProcess?.manufacturingList || []).map((process: any) => ({
                            name: process.processName || '',
                            machineCost: process.totalMachiningCost || 0,
                            setupCost: 0,
                            machineCostType: 'Per Unit' as const,
                            setupCostType: 'Fixed' as const,
                            reference: process.description || ''
                        })),
                        additionalCosts: (quoteAssistModel.additionalRequirements?.additionalRequirementList || []).map((cost: any) => ({
                            name: cost.processName || '',
                            cost: cost.estimatedCost || 0,
                            costType: 'Fixed' as const,
                            reference: cost.purpose || ''
                        }))
                    }
                };
            });

            // Transform cost breakdown - use first part's quoteAssistModel for overall breakdown
            const firstPart = rfqPartList[0];
            const firstQuoteAssistModel = firstPart?.quoteAssistData?.quoteAssistModel || {};
            
            const costBreakdown: CostBreakdown = {
                materials: {
                    totalCost: firstQuoteAssistModel.materials?.totalCost || 0,
                    items: (firstQuoteAssistModel.materials?.materialsList || []).map((material: any) => ({
                        name: material.name || '',
                        cost: material.totalMaterialCost || 0,
                        description: material.description || ''
                    }))
                },
                tooling: {
                    totalCost: firstQuoteAssistModel.tooling?.totalCost || 0,
                    items: (firstQuoteAssistModel.tooling?.toolingList || []).map((tool: any) => ({
                        name: tool.toolName || '',
                        cost: tool.estimatedCost || 0,
                        description: tool.purpose || ''
                    }))
                },
                manufacturing: {
                    totalCost: firstQuoteAssistModel.manufacturingProcess?.totalCost || 0,
                    items: (firstQuoteAssistModel.manufacturingProcess?.manufacturingList || []).map((process: any) => ({
                        name: process.processName || '',
                        cost: process.totalMachiningCost || 0,
                        description: process.description || ''
                    }))
                },
                labor: {
                    totalCost: firstQuoteAssistModel.labor?.totalCost || 0,
                    items: (firstQuoteAssistModel.labor?.laborList || []).map((labor: any) => ({
                        name: labor.name || '',
                        cost: labor.cost || 0,
                        description: labor.description || ''
                    }))
                },
                overhead: {
                    totalCost: firstQuoteAssistModel.overhead?.totalCost || 0,
                    items: (firstQuoteAssistModel.overhead?.overheadList || []).map((overhead: any) => ({
                        name: overhead.overheadTitle || '',
                        cost: overhead.estimatedCost || 0,
                        description: overhead.purpose || ''
                    }))
                },
                otherCosts: {
                    totalCost: firstQuoteAssistModel.otherCosts?.totalCost || 0,
                    items: (firstQuoteAssistModel.otherCosts?.otherCostList || []).map((cost: any) => ({
                        name: cost.processName || '',
                        cost: cost.estimatedCost || 0,
                        description: cost.description || ''
                    }))
                },
                profit: {
                    profitMargin: firstQuoteAssistModel.profit?.industryStandardProfitMargin || 15,
                    totalProfit: firstQuoteAssistModel.profit?.totalPrice || 0
                }
            };

            return {
                rfqId: response.data.rfqId.toString(),
                sourceType: response.data.source as RfqSource,
                lastUpdated: new Date().toISOString(),
                quoteDetails,
                partDetails,
                costBreakdown
            };
        } catch (error) {
            console.error('Error transforming marketplace data:', error);
            throw new Error('Failed to parse marketplace data');
        }
    }

    /**
     * Transform direct RFQ data to QuoteAssistV2Data format
     * @param response Direct RFQ response
     * @returns Transformed QuoteAssistV2Data
     */
    transformDirectData(response: DirectRfqResponse,integration:RfqSource,websiteRFQData:any): QuoteAssistV2Data {
        try {
            let rfqPartsMetaData = response.data.rfqPartsMetaData;
            if(!rfqPartsMetaData && integration === 'SUW'){
                rfqPartsMetaData = this.getWebsiteRfqPartDefaultMetaData(websiteRFQData)
            }
            
            const metaData = JSON.parse(rfqPartsMetaData);
            const rfqDataObj= {
                rfqId: response.data.rfqId,
                sourceType: response.data.source as RfqSource,
                lastUpdated: metaData.lastUpdated || new Date().toISOString(),
                currentStep: metaData.currentStep || 'details',
                quoteDetails: metaData.quoteDetails || {
                    rfqId: response.data.rfqId,
                    rfqName: '',
                    companyName: '',
                    companyEmail: '',
                    companyPhone: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    quoteNumber: '',
                    quoteExpiryDate: '',
                    paymentTerm: '',
                    companyId: response.data.companyId,
                    contactId: response.data.supplierId
                },
                partDetails: metaData.partDetails || [],
                costBreakdown: metaData.costBreakdown || {
                    materials: { totalCost: 0, items: [] },
                    tooling: { totalCost: 0, items: [] },
                    manufacturing: { totalCost: 0, items: [] },
                    labor: { totalCost: 0, items: [] },
                    overhead: { totalCost: 0, items: [] },
                    otherCosts: { totalCost: 0, items: [] },
                    profit: { profitMargin: 15, totalProfit: 0 }
                },
                stepProgress: metaData.stepProgress || []
            };
            
        return rfqDataObj;
        } catch (error) {
            console.error('Error transforming direct data:', error);
            throw new Error('Failed to parse direct data');
        }
    }

    /**
     * Upload part files to the backend
     * @param formData FormData object containing files and part details
     * @returns Observable with response containing rfqId and file URLs
     */
    uploadPartFiles(formData: FormData): Observable<FileUploadResponse> {
        return new Observable<FileUploadResponse>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'RFQ/SaveDirectRFQDetailsV2',
                    formData
                ).subscribe({
                    next: (response) => {
                        // Extract content from rfqPartDetails if available
                        let extractedContent = response.data?.extractedContent || '';
                        let rfqPartDetails = [];

                        if (response.data?.rfqPartDetails) {
                            rfqPartDetails = response.data.rfqPartDetails;
                            
                            // Extract content from files in rfqPartDetails
                            const allExtractedContent: string[] = [];
                            
                            rfqPartDetails.forEach((part: any) => {
                                if (part.files && Array.isArray(part.files)) {
                                    part.files.forEach((file: any) => {
                                        if (file.extractedContent) {
                                            const content = typeof file.extractedContent === 'string' 
                                                ? file.extractedContent 
                                                : JSON.stringify(file.extractedContent);
                                            allExtractedContent.push(content);
                                        }
                                    });
                                }
                            });

                            // If we found extracted content in rfqPartDetails, use it
                            if (allExtractedContent.length > 0) {
                                extractedContent = allExtractedContent.join('\n\n---\n\n');
                            }
                        }

                        const result: FileUploadResponse = {
                            success: true,
                            data: {
                                rfqId: response.data?.rfqId?.toString() || '',
                                fileUrls: response.data?.fileUrls || [],
                                extractedContent: extractedContent,
                                rfqPartDetails: rfqPartDetails,
                                message: response.message || 'Files uploaded successfully'
                            }
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: FileUploadResponse = {
                            success: false,
                            data: {
                                rfqId: '',
                                message: 'Failed to upload files'
                            },
                            error: err.message || 'Unknown error occurred'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: FileUploadResponse = {
                    success: false,
                    data: {
                        rfqId: '',
                        message: 'Failed to upload files'
                    },
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    /**
     * Extract metadata from PDF files
     * @param formData FormData object containing PDF file
     * @returns Observable with extracted content
     */
    extractPdfMetadata(formData: FormData): Observable<MetadataExtractionResponse> {
        return new Observable<MetadataExtractionResponse>(observer => {
            try {
                this.apiService.post<any>(
                    this.baseApiUrl,
                    'extract',
                    formData
                ).subscribe({
                    next: (response) => {
                        // Handle the actual API response structure
                        // The API returns an array with the extracted content as the first element
                        let extractedContent = '';
                        if (Array.isArray(response) && response.length > 0) {
                            extractedContent = response[0] || '';
                        } else if (response.data?.extractedContent) {
                            // Fallback for other response structures
                            extractedContent = response.data.extractedContent;
                        } else if (typeof response === 'string') {
                            // Fallback for direct string response
                            extractedContent = response;
                        }
                        
                        const result: MetadataExtractionResponse = {
                            success: true,
                            data: {
                                extractedContent: extractedContent,
                                message: 'PDF metadata extracted successfully'
                            }
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: MetadataExtractionResponse = {
                            success: false,
                            data: {
                                extractedContent: '',
                                message: 'Failed to extract PDF metadata'
                            },
                            error: err.message || 'Unknown error occurred'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: MetadataExtractionResponse = {
                    success: false,
                    data: {
                        extractedContent: '',
                        message: 'Failed to extract PDF metadata'
                    },
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    /**
     * Get cost breakdown from LLM
     * @param extractedContent Extracted content from PDF
     * @returns Observable with cost breakdown data
     */
    getCostBreakdown(extractedContent: string): Observable<CostBreakdownResponse> {
        return new Observable<CostBreakdownResponse>(observer => {
            try {
                // For MFGDirect integration, use the stored extractedPdfJsonPayload (same structure as V1)
                // For other integrations, use the simple extractedContent payload
                const payload = extractedContent 
                    ? { input: extractedContent }
                    : { input: this.extractedPdfJsonPayload };
                
                this.apiService.post<any>(
                    this.baseApiUrl,
                    'single-response-v2',
                    payload
                ).subscribe({
                    next: (response) => {
                        const result: CostBreakdownResponse = {
                            success: true,
                            data: {
                                costBreakdown: response || {},
                                message: response.message || 'Cost breakdown generated successfully'
                            }
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: CostBreakdownResponse = {
                            success: false,
                            data: {
                                costBreakdown: {},
                                message: 'Failed to generate cost breakdown'
                            },
                            error: err.message || 'Unknown error occurred'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: CostBreakdownResponse = {
                    success: false,
                    data: {
                        costBreakdown: {},
                        message: 'Failed to generate cost breakdown'
                    },
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    /**
     * Set metadata for PDF extraction (MFGDirect integration only)
     * @param value Combined supplier and extracted PDF data
     */
    setMetadata(value: string): void {
        this.extractedPdfJsonPayload = value;
    }

    /**
     * Get the stored PDF JSON payload
     * @returns The stored payload or null if not set
     */
    getExtractedPdfJsonPayload(): string | null {
        return this.extractedPdfJsonPayload;
    }

    /**
     * Get metadata for RFQ (same as V1 getMetadata function)
     * @param rfqId RFQ ID
     * @param companyId Company ID
     * @returns Observable with metadata response
     */
    getMetadata(rfqId: string, companyId: number): Observable<any> {
        return this.apiService.get<any>(
            this.dynamoBaseApiUrl, 
            'supplier/GetQADirectRfqAndSupplierDetailsV2?rfqId=' + rfqId + '&companyId=' + companyId
        );
    }
    getMetadataRev(rfqId: string, companyId: number): Observable<any> {
        return this.apiService.get<any>(
            this.dynamoBaseApiUrl, 
            'supplier/GetQARfqAndSupplierDetailsV2?rfqId=' + rfqId + '&companyId=' + companyId
        );
    }
    getRfqVersion(rfqId: string, source: string): Observable<RfqVersionResponse> {
        return this.apiService.get<RfqVersionResponse>(
            this.dynamoBaseApiUrl,
            `supplier/GetRfqVersion?rfqId=${rfqId}&source=${source}`
        );
    }

    downloadPdfV2(companyId: number, rfqId: string,contactId:number): Observable<any> {
        return this.apiService.get<any>(
            this.dynamoBaseApiUrl,
            `supplier/GetSupplierDirectRfqQuotePdfDataV2?companyId=${companyId}&rfqId=${rfqId}&contactId=${contactId}`
        );
    }

    draftMarketplaceRFQQuoteData(quoteAssistModel: any):Observable<number> {
        const draftData = JSON.stringify(quoteAssistModel);
        return new Observable<number>(observer => {
            try {
                this.apiService.post<QuoteAssistV2Data>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierRfqQuoteData',
                    draftData
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Save marketplace RFQ quote data using V2 API endpoint
     * @param request SaveQASupplierRfqQuoteDataV2Request object
     * @returns Observable with response containing quote ID
     */
    draftMarketplaceRFQQuoteDataV2(request: SaveQASupplierRfqQuoteDataV2Request): Observable<number> {
        return new Observable<number>(observer => {
            try {
                // const quoteStatusMetaData = request.quoteStatus;
                // let rfqPartsMetaData = JSON.parse(request.rfqPartsMetaData);
                // rfqPartsMetaData.quoteStatus = quoteStatusMetaData;
                // request.rfqPartsMetaData = JSON.stringify(rfqPartsMetaData);

                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierRfqQuoteDataV2',
                    request
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Save marketplace draft for MFG integration
     * @param draftData Marketplace draft data
     * @returns Observable with save response
     */
    saveMarketplaceDraft(draftData: MarketplaceDraftModel): Observable<SaveMarketplaceDraftResponse> {
        return new Observable<SaveMarketplaceDraftResponse>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierRfqQuoteData',
                    draftData
                ).subscribe({
                    next: (response) => {
                        const result: SaveMarketplaceDraftResponse = {
                            success: true,
                            data: response
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: SaveMarketplaceDraftResponse = {
                            success: false,
                            error: err.message || 'Failed to save marketplace draft'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: SaveMarketplaceDraftResponse = {
                    success: false,
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                };
                observer.error(result);
            }
        });
    }

    /**
     * Send quote email to buyer
     * @param request SendQuoteEmailToBuyerRequest object containing email details
     * @returns Observable with response indicating success/failure
     */
    sendQuoteEmailToBuyer(request: SendQuoteEmailToBuyerRequest): Observable<SendQuoteEmailToBuyerResponse> {
        return new Observable<SendQuoteEmailToBuyerResponse>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'supplier/SendQuoteEmailToBuyer',
                    request
                ).subscribe({
                    next: (response) => {
                        const result: SendQuoteEmailToBuyerResponse = {
                            success: true,
                            data: response,
                            message: response.message || 'Quote email sent successfully'
                        };
                        observer.next(result);
                        observer.complete();
                    },
                    error: (err) => {
                        const result: SendQuoteEmailToBuyerResponse = {
                            success: false,
                            error: err.message || 'Failed to send quote email',
                            message: 'Failed to send quote email to buyer'
                        };
                        observer.error(result);
                    }
                });
            } catch (error) {
                const result: SendQuoteEmailToBuyerResponse = {
                    success: false,
                    error: error instanceof Error ? error.message : 'Unknown error occurred',
                    message: 'Failed to send quote email to buyer'
                };
                observer.error(result);
            }
        });
    }
    getWebsiteAndPrivateRfqQuotePDF(params: PdfDownloadParams): Observable<any> {
        const url = `${this.dynamoBaseApiUrl}supplier/GetPrivateRfqQuotePDF?version=${params.version}&companyId=${params.companyId}&rfqId=${params.rfqId}&supplierId=${params.contactId}`;
        
        return this.http.get(url, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            catchError(error => throwError(() => error)),
            timeout(300000)
        );
    }
    downloadPartAllFiles(params: any): Observable<any> {
        const url = `${this.dynamoBaseApiUrl}rfq/DownloadAllFile`
        return this.apiService.postData(url, params, );
    }

    /**
     * Get RFQ quote intelligence (e.g. risk flags).
     * Backend signature:
     *   [HttpGet]
     *   [Route("GetRFQQuoteIntelligence")]
     *   public async Task<IActionResult> GetRFQQuoteIntelligence(int rfqId, int companyId)
     *
     * @param payload RfqId, CompanyId (RfqPartId is not used by this endpoint)
     * @returns Observable with intelligence/risk card data
     */
    getRfqQuoteIntelligence(payload: GetRfqQuoteIntelligenceRequest): Observable<GetRfqQuoteIntelligenceResponse> {
        return this.apiService.get<GetRfqQuoteIntelligenceResponse>(
            this.dynamoBaseApiUrl,
            'rfq/GetRFQQuoteIntelligenceRiskFactors',
            {
                rfqId: payload.RfqId,
                companyId: payload.CompanyId,
                contactId: payload.ContactId
            }
        );
    }

    /**
     * Save card feedback (like/dislike) for quote intelligence card
     * @param payload rfqId, rfqPartId, contactId, cardName, cardData (string), action
     */
    saveCardFeedback(payload: SaveCardFeedbackRequest): Observable<any> {
        return this.apiService.post<any>(
            this.dynamoBaseApiUrl,
            'rfq/SaveCardFeedback',
            payload
        );
    }

    quoteAssistV2Data:any={}
    saveQuoteAssistV2Progress(params: any): Observable<any> {

        return new Observable<string>(observer => {
            this.quoteAssistV2Data[params.rfqId]=params;
            observer.next(params.rfqId);
            observer.complete();
           
        });
    }
    getQuoteAssistV2Progress(params: any): Observable<any> {
        
        return new Observable<string>(observer => {
            
            observer.next(this.quoteAssistV2Data[params.rfqId]?this.quoteAssistV2Data[params.rfqId]:null);
            observer.complete();
           
        });
    }

 
    saveFeedback(request: any): Observable<any> {
        return this.apiService.post<any>(
            this.dynamoBaseApiUrl,
            'Supplier/saveFeedback',
            request
        );
    }

    /**
     * Check if survey feedback exists for a contact
     * @param contactId Contact ID to check
     * @returns Observable with response indicating if survey exists
     */
    checkSurveyFeedbackExists(contactId: number): Observable<SurveyFeedbackExistsResponse> {
        return this.apiService.get<SurveyFeedbackExistsResponse>(
            this.dynamoBaseApiUrl,
            `Supplier/CheckSurveyFeedbackExists?contactId=${contactId}`
        );
    }

    /**
     * Combine buyerCompanyAddress and buyerCompanyAddress2 for Company Address display.
     */
    private formatBuyerCompanyAddress(data: {
        buyerCompanyAddress?: string;
        buyerCompanyAddress2?: string;
    } | null | undefined): string {
        const line1 = (data?.buyerCompanyAddress || '').trim();
        const line2 = (data?.buyerCompanyAddress2 || '').trim();
        if (line1 && line2) {
            return `${line1}, ${line2}`;
        }
        return line1 || line2;
    }

    getWebsiteRfqPartDefaultMetaData(quoteDetails: any): string {
             let partDetails:any[]=[];
          const rfqPartDetails=JSON.parse(quoteDetails.rfqMetaData)[0];//consider always one part
         let partObject={
                    "partId":rfqPartDetails.rfq_part_id,
                    "partName":rfqPartDetails.part_name,
                    "partNumber":rfqPartDetails.part_number,
                    "files":rfqPartDetails.pdf_files,
                    "quantityPricing":[
                        {quantity:rfqPartDetails.qty1,leadtimeValue:0,leadtimeUnit:'Days',unitCost:0,markup:null,unitPrice:0,total:0,order:1,unitPriceTotal:0,isQuantityEditable:true,isValidQuantity:true,showLeadTimeInputFields:false},
                        {quantity:null,leadtimeValue:0,leadtimeUnit:'Days',unitCost:0,markup:null,unitPrice:0,total:0,order:2,unitPriceTotal:0,isQuantityEditable:true,isValidQuantity:true,showLeadTimeInputFields:false},
                        {quantity:null,leadtimeValue:0,leadtimeUnit:'Days',unitCost:0,markup:null,unitPrice:0,total:0,order:3,unitPriceTotal:0,isQuantityEditable:true,isValidQuantity:true,showLeadTimeInputFields:false}
                    ]
            };
            partDetails.push(partObject);
            const materialItem={"name":rfqPartDetails.material,"cost":null,"costType":"Per Unit","reference":""};
            const processesItem={"name":null,"cost":null,"costType":"Per Unit","reference":""};
           
            const data={
            "rfqId":quoteDetails.rfqId,
            "sourceType":"SUW",
            "lastUpdated":'',
            "quoteDetails":{
                "rfqId":quoteDetails.rfqId,
                "rfqName":quoteDetails.rfqName,
                "companyName":quoteDetails.buyerCompanyName,
                "companyEmail":quoteDetails.buyerCompanyEmail,
                "companyPhone":quoteDetails.buyerCompanyPhone,
                "address": this.formatBuyerCompanyAddress(quoteDetails),
                "city":quoteDetails.buyerCompanyCity,
                "state":quoteDetails.buyerCompanyState,
                "zipCode":quoteDetails.buyerCompanyZipCode,
                    "quoteNumber":'',
                    "quoteExpiryDate":"",
                    "quoteDate":"",
                    "paymentTerm":quoteDetails.paymentTerms,
                    "isDraftEnabled":false,
                    "isSubmitEnabled":false,
                    "isQuoteDetailsCompleted":false
                },
                "partDetails":partDetails,
                "costBreakdown":{
                            "materials":{"totalCost":0,"items":[materialItem]},
                            "tooling":{"totalCost":0,"items":[]},
                            "processes":{"totalCost":0,"items":[processesItem]},
                            "manufacturing":{"totalCost":0,"items":[]},
                            "labor":{"totalCost":0,"items":[]},
                            "overhead":{"totalCost":0,"items":[]},
                            "otherCosts":{"totalCost":0,"items":[]},  
                            "profit":{"profitMargin":15,"totalProfit":0}
                            },
                "isConfirmQuote":false,"currentStep":"details","currentPartIndex":0,
                "stepProgress":[
                    {"stepId":"details","title":"Add Quote Details","status":"pending","isAccessible":true,"validationErrors":[]},
                    {"stepId":"parts-information","title":"Parts","status":"pending","isAccessible":true,"validationErrors":[]},
                    {"stepId":"costs","title":"Confirm Quote","status":"pending","isAccessible":false,"validationErrors":[]}
                    ],
                "overallProgress":0,
                "lastActiveTimestamp":''}
        return JSON.stringify(data);
    }

    /**
     * Save company metadata (supplier details) to the backend
     * @param companyMetadata Metadata object containing supplier information
     * @returns Observable with response number
     */
    saveCompanyMetaData(companyMetadata: any): Observable<number> {
        return new Observable<number>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierDetails',
                    companyMetadata
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }
} 